import type { ReactNode } from 'react';
import { Building2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router';

import { DEAL_COST_TYPE_LABELS } from '@/types/deal-costs';
import {
  FUND_TRANSACTION_TYPE_LABELS,
  type FundTransactionDetail,
  type ReconciliationStateResponse,
} from '@/types/fund-transactions';
import { formatCurrency } from '@/utils/formatters';

import { Field } from './transactionDetails';
import { TransactionDetailsCard } from './TransactionDetailsCard';
import { Button } from '@/components/ui/button';

type GenericCostDetailsCardProps = {
  transaction: FundTransactionDetail;
  fundName: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries?: () => void;
  firstField?: { label: string; value: ReactNode };
  secondField?: { label: string; value: ReactNode };
  actions?: ReactNode;
};

function GenericCostDetailsCard({
  transaction,
  fundName,
  reconciliation,
  onViewJournalEntries,
  firstField,
  secondField,
  actions,
}: GenericCostDetailsCardProps) {
  return (
    <TransactionDetailsCard
      transaction={transaction}
      actions={actions}
      fundName={fundName}
      reconciliation={reconciliation}
      onViewJournalEntries={onViewJournalEntries}
      icon={<Building2 className="h-5 w-5" />}
      iconClassName="bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300"
      eyebrow={FUND_TRANSACTION_TYPE_LABELS[transaction.transaction_type]}
      fields={
        <>
          <Field label="Amount">
            {formatCurrency(transaction.amount, {
              currency: transaction.currency,
            })}
          </Field>
          {firstField && (
            <Field label={firstField.label}>{firstField.value}</Field>
          )}
          {secondField && (
            <Field label={secondField.label}>{secondField.value}</Field>
          )}
        </>
      }
    />
  );
}

type CostDetailsCardProps = {
  transaction: FundTransactionDetail;
  fundName: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries?: () => void;
};

export function DealCostDetailsCard({
  transaction,
  fundName,
  reconciliation,
  onViewJournalEntries,
}: CostDetailsCardProps) {
  const snapshot = transaction.deal_cost;

  return (
    <GenericCostDetailsCard
      transaction={transaction}
      fundName={fundName}
      reconciliation={reconciliation}
      onViewJournalEntries={onViewJournalEntries}
      firstField={
        snapshot
          ? {
              label: 'Portfolio Company',
              value: snapshot.portfolio_company_name ?? '-',
            }
          : undefined
      }
      secondField={
        snapshot
          ? {
              label: 'Cost Type',
              value: DEAL_COST_TYPE_LABELS[snapshot.cost_type],
            }
          : undefined
      }
    />
  );
}

export function DrawdownDetailsCard({
  transaction,
  fundName,
  reconciliation,
  onViewJournalEntries,
}: CostDetailsCardProps) {
  const snapshot = transaction.drawdown;
  if (!snapshot) return null;

  const actions = (
    <div className="flex items-center gap-2">
      <Button asChild variant="outline" size="lg" className="h-9">
        <Link
          to={`/app/funds/${transaction.fund_id}/drawdowns/${snapshot.drawdown_id}/preview`}
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          View drawdown
        </Link>
      </Button>
    </div>
  );

  return (
    <>
      <GenericCostDetailsCard
        transaction={transaction}
        actions={actions}
        fundName={fundName}
        reconciliation={reconciliation}
        onViewJournalEntries={onViewJournalEntries}
        firstField={{
          label: 'Purpose',
          value: snapshot.purpose || '-',
        }}
        secondField={{
          label: 'Drawdown',
          value: `#${snapshot.drawdown_number}`,
        }}
      />
    </>
  );
}
